import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  IonButton,
  IonCol,
  IonIcon,
  IonItem,
  IonRow,
  IonTextarea,
} from '@ionic/angular/standalone';
import { filter, map, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { SortCodeService } from 'src/app/core/services/common/sort-code/sort-code.service';
import { WarehouseOutboundService } from 'src/app/core/services/inbound/warehouse-outbound/warehouse-outbound.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { DataUtils } from 'src/app/core/utils/data/data.utils';
import { FileUploadComponent } from '../../../file-upload/file-upload.component';
import {
  FileParseType,
  FileReturn,
  ParsedFileData,
} from '../../../file-upload/file-upload.constants';
import { DatePickerComponent } from '../../../form/components/date-picker/date-picker.component';
import { FormInputFieldComponent } from '../../../form/components/form-input-field/form-input-field.component';
import { FormSelectComponent } from '../../../form/components/form-select/form-select.component';
import { IOption } from '../../../form/components/form-select/form-select.model';
import { IMAGE_SRC } from '../../../shared-constants';
import { isAnyInvalidTrackingNumber } from '../../../validators';
import { CONSTS } from './add-new-scan.constants';
import { FileUploadButtonComponent } from './file-upload-button/file-upload-button.component';

@Component({
  selector: 'app-add-new-scan',
  templateUrl: './add-new-scan.component.html',
  imports: [
    DatePickerComponent,
    FormSelectComponent,
    FormInputFieldComponent,
    ReactiveFormsModule,
    IonItem,
    IonTextarea,
    IonButton,
    IonRow,
    IonCol,
    IonIcon,
    CommonModule,
    FileUploadButtonComponent,
  ],
  styleUrls: ['./add-new-scan.component.scss'],
})
export class AddNewScanComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() isInvalidTrackingNumber = false;
  @Input() isFormSubmitted = false;
  @Input() minSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  @Input() maxSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 3,
  );
  @Input() optionsList: IOption[] = [];
  @Input() hasLocationControl = false;
  @Input() hasButton = true;
  @Input() hasFileUpload = true;
  @Input() hasSingleTrackingNumber = false;
  @Input() hasFileUploadButton = false;
  @Input() isSingleLineFormControl = true;
  @Input() triggerAPIOnSubmit = false;
  @Input() isTrackingNumberExistInTable = false;
  @Input() disableSubmit = false;
  @Input() isPTN = false;
  @Input({ required: true }) fileType!: FileParseType;

  @Output() emitSubmit = new EventEmitter<FileReturn>();
  @Output() emitFileUpload = new EventEmitter<FileReturn>();

  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);
  readonly _warehouseOutboundService = inject(WarehouseOutboundService);
  private readonly _sortCodeService = inject(SortCodeService);
  private readonly _dataUtils = inject(DataUtils);

  readonly CONSTS = CONSTS;
  readonly SRC = IMAGE_SRC;
  readonly SpecialCharacters = SpecialCharacters;
  readonly Validators = Validators;

  constructor(
    public commonUtils: CommonUtils,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.subscribes();
    this.getCurrentUser();
  }
  /**
   * used to subscribes async data from service
   *
   * @memberof TestTrackingMaintenanceTableComponent
   */
  subscribes() {
    this._dataUtils.uploadTrackingNumbers$
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((file) => {
        if (file) {
          this.emitFileUpload.emit(file);
        }
      });
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(() => {
        if (
          this.hasLocationControl &&
          this.form.get('trackingNumbers')?.valid &&
          this.form.get('trackingNumbers')?.value?.length > 0 &&
          this.form.get('trackingNumbers')?.dirty
        ) {
          this.form.get('location')?.addValidators(Validators.required);
        }

        if (
          this.hasLocationControl &&
          this.form.get('trackingNumbers')?.value?.length === 0 &&
          this.form.get('trackingNumbers')?.dirty
        ) {
          this.form.get('location')?.removeValidators(Validators.required);
        }

        if (
          this.hasLocationControl &&
          this.form.get('location')?.valid &&
          this.form.get('location')?.value?.length > 0 &&
          this.form.get('location')?.dirty
        ) {
          this.form.get('trackingNumbers')?.addValidators(Validators.required);
        }

        if (
          this.hasLocationControl &&
          this.form.get('location')?.value?.length === 0 &&
          this.form.get('location')?.dirty
        ) {
          this.form
            .get('trackingNumbers')
            ?.removeValidators(Validators.required);
        }
      });
  }

  /**
   * get current user
   *
   * @memberof UldExtractComponent
   */
  getCurrentUser() {
    this.userService
      .getCurrentUser$()
      .pipe(
        filter((user) => !!user),
        takeUntilDestroyed(this.destroy),
        map((_user) => {
          this.getSortCodes();
        }),
      )
      .subscribe();
  }

  /**
   * Used to get sort codes
   *
   * @private
   * @memberof AddNewScanComponent
   */
  private getSortCodes() {
    this.optionsList = [];
    return this._sortCodeService
      .getSortCodes()
      .pipe(
        map((sortCodeResponse) => {
          const sortCodes = sortCodeResponse?.response?.sortCodes || [];
          if (sortCodes.length) {
            this.optionsList = [...sortCodes].map((sortCode) => {
              const option = {
                label: sortCode.sorttype,
                value: sortCode.sorttype,
              };
              return option;
            });
            this.form.get('sortCode')?.setValue(this.optionsList?.[0]?.value);
            return;
          }

          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }),
      )
      .subscribe();
  }

  /**
   * Used to open file upload modal
   *
   * @memberof InboundScanTableComponent
   */
  fileUpload() {
    this.dialog
      .open(FileUploadComponent, {
        data: { trackingNumber: true, fileType: this.fileType },
        disableClose: true,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((fileContents: FileReturn) => {
        if (fileContents) {
          this.emitFileUpload.emit(fileContents);
        }
      });
  }

  /**
   * Reset validation flag
   *
   * @memberof InboundScanTableComponent
   */
  resetValidationFlag() {
    this.isFormSubmitted = false;
    this.isInvalidTrackingNumber = false;
    this.isTrackingNumberExistInTable = false;
  }

  /**
   * On clicking submit button emit the button click to be handled in parent
   *
   * @memberof InboundScanTableComponent
   */
  clickSubmit() {
    const trackingNumberData = this.form?.value?.trackingNumbers
      .toString()
      .trim();
    //Make sure location is correctly uppercased
    if (this.hasLocationControl) {
      this.form
        .get('location')
        ?.setValue(this.form.get('location')?.value.toUpperCase());
    }
    // Check for any invalid tracking numbers
    if (!this.isPTN) {
      this.isInvalidTrackingNumber =
        isAnyInvalidTrackingNumber(trackingNumberData);
    }
    const returnVal: FileReturn = {
      fileName: '',
      fileSize: 0,
      returnData: this.mapTrackingNumbers(trackingNumberData.split(',')),
    };
    this.emitSubmit.emit(returnVal);
    if (this.form.valid) {
      this.form.get('trackingNumbers')?.reset();
    }
  }

  /**
   * Maps tracking numbers to an array of objects containing sort code, sort date, tracking number, and optionally location.
   * @param {string} trackingNumberData - Comma-separated string of tracking numbers.
   * @returns {Array<Object>} - Array of mapped tracking number objects.
   */
  mapTrackingNumbers(trackingNumberData: string[]): ParsedFileData[][] {
    this.isFormSubmitted = true;
    // Map tracking numbers to the required format
    return [
      trackingNumberData.map((trackingNumber: string) => ({
        trackingNumber: trackingNumber.toUpperCase(),
      })),
    ];
  }

  changeToUpperCase() {
    const trackingNumberControl = this.form.get('trackingNumbers');
    trackingNumberControl?.setValue(
      trackingNumberControl?.value?.toUpperCase(),
    );
  }
}
